<template>
  <div
    ref="datatableRef"
    :class="[
      'datatable-v3 rounded-lg bg-white',
      {
        scrollable,
        'fixed-right-column': hasActionsColumn,
        'shadow-left': state.shadows.left,
        'shadow-right': state.shadows.right,
        'shadow-top': state.shadows.top,
        'shadow-bottom': state.shadows.bottom,
        'opacity-0': !stylesLoaded,
        'no-data': state.resultCount === 0
      }
    ]"
  >
    <header
      v-if="!hideHeader"
      ref="header"
      class="flex gap-2.5 items-center pb-4 w-full font-medium"
    >
      <div
        class="flex flex-wrap flex-1 px-6 shrink md:gap-4 justify-between items-start self-stretch my-auto w-full basis-0 min-w-[240px] max-md:max-w-full"
      >
        <h2
          class="page-header-1 datatable-title mr-3 mb-3 md:mr-0 md:mb-0"
          :class="{ 'page-header-2': subpage }"
          :data-cy="title"
        >
          {{ title }}
        </h2>
        <div
          class="flex gap-4 items-end self-stretch my-auto text-sm flex-wrap"
        >
          <template v-if="!hideGlobalFilters">
            <DatatableFilter
              v-for="filter in globalFilters"
              :key="filter.name"
              :filter="filter"
              :data-cy="`${filter.name}_column_filter`"
              class="toggle-column-filter"
              :class="{ 'mb-2': filter.type === 'toggle' }"
              :version="2"
              @refresh-table="refreshData"
            />
          </template>
          <button
            v-if="!hideFilters"
            class="flex relative gap-2 justify-center items-center p-4 md:px-5 md:py-2.5 text-gray-900 whitespace-nowrap bg-white rounded-full md:rounded-lg border border-solid"
            data-cy="datatableFilterBtn"
            @click="toggleFiltersOpen"
          >
            <CloseIcon v-if="state.filtersOpen" height="12" />
            <SearchIcon v-else class="h-3" />
            <span class="hidden md:block">Filter</span>

            <div
              v-if="
                state.appliedFilters.length ||
                Object.keys(state.searches).length
              "
              :class="[
                'inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-blue-900 rounded-full',
                {
                  'absolute rounded-full -top-2 -end-2': breakpoints().sm
                }
              ]"
            >
              {{
                state.appliedFilters.length + Object.keys(state.searches).length
              }}
            </div>
          </button>
          <slot name="leftActions"></slot>
          <slot name="rightActions"
            ><div
              class="dtRightActions hidden flex flex-col gap-4 md:flex-row md:items-center"
            ></div
          ></slot>
          <slot name="actions"></slot>
          <HintTooltip
            v-if="!noDownload"
            ref="hintTooltip"
            text="This is already queued"
          >
            <template #target>
              <Button
                class="md:rounded-lg rounded-full md:w-auto md:h-auto w-[46px] h-[46px]"
                @click="exportReport"
              >
                <div class="flex items-center gap-1">
                  <i class="fas fa-download" />
                  <span class="hidden md:block">Download</span>
                </div>
              </Button>
            </template>
          </HintTooltip>
        </div>
      </div>
    </header>
    <div :class="['col-filter', { active: tagsVisible }]">
      <section
        ref="appliedFilters"
        class="flex flex-wrap gap-4 justify-between items-center py-4 px-6 font-medium border-t"
      >
        <div class="flex flex-wrap gap-1.5 items-center text-indigo-900">
          <span class="text-sm text-gray-900">Filtering by:</span>
          <div
            v-for="filter in state.appliedFilters"
            :key="filter.name"
            class="bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs font-medium px-4 py-2 flex items-center gap-4 rounded-lg dark:bg-blue-900 dark:text-blue-300 !cursor-pointer"
            @click="removeFilter(filter)"
          >
            {{ filterLabel(filter) }}
            <CloseIcon height="10" />
          </div>
          <div
            v-for="(value, name) in state.searches"
            :key="name"
            class="bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs font-medium px-4 py-2 flex items-center gap-4 rounded-lg dark:bg-blue-900 dark:text-blue-300 !cursor-pointer"
            @click="clearSearch(name)"
          >
            {{ searchTermLabel(name) }}: "{{ value }}"
            <CloseIcon height="10" />
          </div>
        </div>
        <button
          class="gap-1 self-stretch py-0.5 pr-1.5 pl-1.5 my-auto text-gray-600 rounded hidden md:block"
          @click="clearAllFilters"
        >
          Clear All Filters
        </button>
      </section>
    </div>
    <table :id="id" ref="table" style="min-width: 100%">
      <thead>
        <tr>
          <th
            v-for="(col, idx) in tableColumnsConfig"
            :key="`col_${col.data}`"
            :ref="
              (el) => {
                headerRefs[`header_${col.data}`] = el;
              }
            "
            :class="[
              col.headerClassName || col.className,
              `${id}_header_${col.data}`,
              { 'filter-active': hasFilterApplied(col) },
              { 'actions-column-header': col.data === 'actions' },
              { 'filters-open h-[100px]': state.filtersOpen },
              { 'h-[50px]': !state.filtersOpen },
              getSortClasses(col, idx)
            ]"
          >
            <p v-if="!col.checkbox">
              {{ capitalize(col.title || col.data) }}
            </p>
            <div v-if="col.checkbox" class="datatable-header-checkbox">
              <DatatableCheckboxV2
                :disabled="headerCheckboxDisabled"
                :modelValue="headerCheckboxValue"
                class="datatable-checkbox"
                @update:modelValue="handleSelectAll"
              />
            </div>
          </th>
        </tr>
      </thead>
      <tbody ref="tbody" class="table-data" data-cy="table-data" />
      <tfoot v-show="state.hasAggregates" class="datatable-footer-aggregates">
        <tr>
          <th
            v-for="col in tableColumnsConfig"
            :key="col.data"
            class="aggregate-totals-header"
            :class="col.footerClassName"
          ></th>
        </tr>
        <tr>
          <td
            v-for="col in tableColumnsConfig"
            :key="col.data"
            class="aggregate-totals-value"
            :class="[col.footerClassName, col.footerCellClass]"
          ></td>
        </tr>
      </tfoot>
    </table>
    <SafeTeleport
      v-if="!hidePageLengthSelect && state.drawn && !state.drawing"
      :to="`#pageSelectPortal-${id}`"
    >
      <DatatablePageLength
        v-model="state.pageLength"
        :options="pageLengthOptions"
      />
    </SafeTeleport>
    <div v-if="stylesLoaded && state.drawn">
      <div v-for="col in visibleColumns" :key="`filter_${col.data}`">
        <SafeTeleport :to="`.${id}_header_${col.data}`">
          <div
            :ref="
              (el) => {
                filterRefs[`filter_${col.data}`] = el;
              }
            "
            :class="['col-filter', { active: state.filtersOpen }]"
          >
            <div
              :class="[col.filterClassName, 'pt-1.5']"
              @click.stop="handleFilterFocus"
            >
              <div v-if="col.checkbox" class="datatable-header-checkbox">
                <DatatableCheckboxV2
                  :disabled="headerCheckboxDisabled"
                  :modelValue="headerCheckboxValue"
                  class="datatable-checkbox"
                  @update:modelValue="handleSelectAll"
                />
              </div>
              <DatatableFilter
                v-else-if="columnFilter(col)"
                :ref="
                  (el) => {
                    filterComponentRefs[
                      `${columnFilter(col)?.name}FilterComponent`
                    ] = el;
                  }
                "
                :filter="columnFilter(col)"
                :data-cy="`${col.filterName || col.data}_column_filter`"
                no-label
                class="column-filter"
                :version="2"
                @refresh-table="refreshData"
              />
              <div v-else-if="col.searchable !== false">
                <TextField
                  class="full-width"
                  input-class="column-search-field"
                  :data-cy="`${col.filterName || col.data}_column_search`"
                  :placeholder="getSearchPlaceholder(col)"
                  :model-value="getColumnSearchValue(col.data)"
                  search
                  @update:modelValue="handleColSearch($event, col.data)"
                />
              </div>
            </div>
          </div>
        </SafeTeleport>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash";
import API from "serviceshift-ui/api-client";
import SSUIEventBus from "serviceshift-ui/shared/src/lib/event_bus";
import {
  computed,
  createApp,
  defineComponent,
  h,
  markRaw,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onBeforeUpdate,
  reactive,
  ref,
  useSlots,
  watch
} from "vue";
import { useRouter } from "vue-router";
import { SafeTeleport } from "vue-safe-teleport";
import { EventBus } from "@/lib/event_bus";
import { isEmpty, isFalsy } from "@/lib/helpers";
import { extractProps, renderVueComponent } from "@/lib/renderVueComponent.js";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import Button from "@/components/Button.vue";
import DatatablePageLength from "@/components/DatatablePageLength.vue";
import HintTooltip from "@/components/HintTooltip.vue";
import TextField from "@/components/TextField.vue";
import CONSTANTS from "@/Constants";
import SearchIcon from "@/images/search.svg?component";
import CloseIcon from "@/images/x.svg?component";
import { capitalize } from "@/mixins";
import { breakpoints } from "@/mixins";
import DatatableCheckboxV2 from "@/Reports/DatatableCheckboxV2.vue";
import DatatableFilter from "@/Reports/DatatableFilter.vue";
import store from "@/vuex/store";
import { useStorage } from "./useStorage";

const { CALL_MODAL_SEARCH_TERM_KEY } = CONSTANTS;

const router = useRouter();

const STACKED_FOOTER_HEIGHT = 140;
const UNSTACKED_FOOTER_HEIGHT = 100;

interface Config {
  paging?: boolean;
  retrieve?: boolean;
  processing?: boolean;
  serverSide?: boolean;
  pageLength?: number;
  pagingType?: string;
  lengthMenu?: ((number | string)[] | null)[] | null;
  columns: ColumnsEntity[] | null;
  bLengthChange?: boolean;
  language?: Language;
  fixedColumns?: {
    start: string;
    end: string;
  };
  order?: ((number | string)[] | null)[] | null;
  responsive?: boolean;
  layout?: {
    topStart: string;
    topEnd: string;
  };
  scrollX?: boolean;
  scrollY?: boolean;
  colReorder?: boolean;
  autoWidth?: boolean;
  vueComponents?: VueComponentsEntity[] | null;
}
interface ColumnsEntity {
  data: string | null;
  className?: string;
  headerClassName?: string;
  title?: string;
  searchable?: boolean;
  orderable?: boolean;
  checkbox?: boolean;
  filterName?: string;
  filterClassName?: string;
  visible?: boolean;
  searchPlaceholder?: string;
  footerClassName?: string;
  footerCellClass?: string;
  headerEditIcon?: boolean;
  render?: any;
}
interface Language {
  infoFiltered: string;
  paginate: Paginate;
  processing: string;
}
interface Paginate {
  first: string;
  previous: string;
  next: string;
  last: string;
}
interface VueComponentsEntity {
  rootId: string;
  component?: any;
  options: {
    store?: any;
    props?: any;
  };
}
export interface Filter {
  type: string;
  name: string;
  label: string;
  placeholder?: string;
  options?: Array<{ label: string; value: string }>;
  multiple?: boolean;
  value?: string;
  default_value?: string;
}
interface RowEntity {
  checkbox_disabled?: string;
  internalId?: string;
  [key: string]: any;
}

const configDefaults: Config = {
  retrieve: true,
  processing: true,
  serverSide: true,
  pageLength: 25,
  paging: true,
  pagingType: "numbers",
  lengthMenu: [
    [10, 25, 50, 100],
    ["10", "25", "50", "100"]
  ],
  columns: [],
  bLengthChange: false,
  language: {
    infoFiltered: "",
    paginate: {
      first: "«",
      previous: "‹",
      next: "›",
      last: "»"
    },
    processing:
      '<i class="fa-duotone fa-solid fa-spinner fa-spin fa-3x fa-fw text-gray-500"></i><span class="sr-only">Loading...</span>'
  },
  fixedColumns: { start: "0", end: "0" },
  responsive: false,
  layout: {
    topStart: "div",
    topEnd: "div"
  },
  scrollX: false,
  scrollY: false,
  colReorder: false,
  autoWidth: false
};

const emit = defineEmits([
  "download-report",
  "row-select",
  "row-click",
  "after-init",
  "draw-complete",
  "page-change",
  "resize",
  "update:bulk-select-ids"
]);

const props = withDefaults(
  defineProps<{
    scrollableHeight?: string;
    scrollable?: boolean;
    bulkSelectAcrossPages?: boolean;
    // Keeps the items selected as you go from page to page. Similar to GMail.
    bulkSelectKeepAlive?: boolean;
    bulkSelect?: boolean;
    bulkSelectKey?: string;
    bulkSelectIds?: Array<string | number>;
    csvFileName?: string;
    useDatatableExportCsv?: boolean;
    config: Config;
    source: string;
    // Bad name, should be ajax data config. Used to manipulate the data request sent to the server
    mapRecords?: () => Record<string, any>;
    minHeight?: number;
    // Enables row click to select rows
    selectable?: boolean;
    // Highlights a row based on the record.id
    selectedId?: string | number;
    id?: string;
    // Removes CSS card and box-shadow styling
    flat?: boolean;
    // Callback fired after the datatable is reloaded
    onReload?: () => void;
    // Determines if the page should be reset when the datatable is reloaded
    resetPaging?: boolean;
    filters?: Filter[];
    noDownload?: boolean;
    downloadUrl?: string;
    defaultPageLength?: number;
    hideGlobalFilters?: boolean;
    hidePageLengthSelect?: boolean;
    mapRowClass?: () => string;
    mapRowId?: () => string | number;
    // Persists column searches across page loads
    persistSearch?: boolean;
    title?: string;
    // Hides Filter button
    hideFilters?: boolean;
    //hides entire header, used in nested tables
    hideHeader?: boolean;
    autoHeight?: boolean;
    willEmitDownloadReport?: () => boolean;
    maxHeight?: number;
    subpage?: boolean;
  }>(),
  {
    scrollableHeight: "300px",
    bulkSelectKey: "id",
    bulkSelectIds: () => [],
    csvFileName: "orders",
    mapRecords: undefined,
    selectedId: "",
    id: "dt",
    onReload: () => {},
    filters: () => [],
    downloadUrl: "",
    defaultPageLength: 25,
    mapRowClass: () => "",
    mapRowId: () => "",
    title: "",
    autoHeight: true,
    willEmitDownloadReport: () => false,
    maxHeight: undefined,
    minHeight: 200
  }
);

const storage = useStorage();
const state = reactive({
  availableInternalIds: [],
  internalSelectedIds: [] as Array<string | number>,
  rowsInView: [] as RowEntity[],
  customRowVueApps: [] as Array<{ column: string; component: any }>,
  datatable: null as any,
  drawn: false,
  drawing: true,
  selectedNode: null,
  hasAggregates: true,
  pageLength: props.defaultPageLength.toString(),
  filtersVisible: true,
  searches: {},
  isDestroying: false,
  searchCols: [],
  rowSelectionRange: [null, null],
  usedShiftKeyLast: false,
  lastCheckedValue: false,
  appliedFilters: [] as Filter[],
  filtersOpen: false,
  shadows: {
    left: false,
    right: false,
    top: false,
    bottom: false
  },
  resultCount: 0
});

const stylesLoaded = ref(false);
const hintTooltip = ref<any>(null);
const table = ref<any>(null);
const tbody = ref<any>(null);
const filterRefs = ref<any>({});
const filterComponentRefs = ref<any>({});
const headerRefs = ref<any>({});
const datatableRef = ref<any>(null);

const slots = useSlots();

// COMPUTED
const tagsVisible = computed(() =>
  Boolean(state.appliedFilters.length || Object.keys(state.searches).length)
);
const tableColumnsConfig = computed(() => {
  let columns = props.config.columns || [];
  // If we are using a bulk select display, the first column needs to be a checkbox
  if (props.bulkSelect) {
    const colDef = {
      checkbox: true,
      data: null,
      orderable: false,
      searchable: false,
      bSortable: false,
      bSearchable: false,
      title: "",
      sTitle: "",
      render: renderCheckboxCell
    };
    if (columns[0]?.checkbox) {
      columns[0] = colDef;
    } else {
      columns.unshift(colDef);
    }
  }
  columns = columns.map((col) => {
    if (col.data === "actions") {
      col.title = "Action";
      col.searchable = false;
      col.orderable = false;
    }
    if (slots[`column-${col.data}`]) {
      col.render = customColumnRender;
    }
    return col;
  });
  return columns;
});

/**
 * Allows the user to implement a custom template for a column. The template name
 * must match the `data` property in the column configuration.
 * If the user does not define a definition, the default logic is applied where
 * we render the component from the response.
 *
 * ex:
 * { data: "date_added", ... }
 * <template #column-date_added="rowData">...</template>
 *
 */
const customColumnRender = (data: any, type: string, row: any, meta: any) => {
  const column = tableColumnsConfig.value[meta.col].data;
  if (!column) return;

  // eslint-disable-next-line vue/one-component-per-file
  const vueComponent = defineComponent({
    setup(_) {
      return () => {
        const slotContent =
          slots[`column-${column}`]?.({
            ...row,
            columnValue: data
          }) || "";

        return h("div", { class: "datatable-custom-column" }, [slotContent]);
      };
    }
  });
  state.customRowVueApps.push({ column, component: markRaw(vueComponent) });
  return `<div class="datatable-custom-column-${column}-${
    state.customRowVueApps.length - 1
  }">${data}</div>`;
};
const renderCheckboxCell = (data: any, type: string, row: any, meta: any) => {
  const id = row[props.bulkSelectKey];
  const cell = state.datatable.cell({ row: meta.row, column: meta.col });

  // eslint-disable-next-line vue/one-component-per-file
  const vueComponent = defineComponent({
    setup() {
      return () =>
        h("div", [
          h(DatatableCheckboxV2, {
            "onUpdate:modelValue": () => handleSelect(row),
            onClick: (e) => handleSelectRange(e, cell),
            modelValue: state.internalSelectedIds.indexOf(id as never) > -1,
            disabled: row.checkbox_disabled === "true"
          })
        ]);
    }
  });

  state.customRowVueApps.push({
    column: "bulk-select-checkbox",
    component: markRaw(vueComponent)
  });
  return `<div class="datatable-custom-column-bulk-select-checkbox-${
    state.customRowVueApps.length - 1
  }"></div>`;
};
const visibleColumns = computed(() =>
  tableColumnsConfig.value.filter((col) => col.visible !== false)
);
const checkableRowsInView = computed(() =>
  state.rowsInView.filter((row) => row.checkbox_disabled !== "true")
);
const hasSelectedAll = computed(() => {
  if (props.bulkSelectAcrossPages) {
    return state.availableInternalIds.every((id) => {
      return state.internalSelectedIds.includes(id);
    });
  } else {
    return checkableRowsInView.value.every((row) => {
      return (
        state.internalSelectedIds.indexOf(row[props.bulkSelectKey] as never) >
        -1
      );
    });
  }
});
const headerCheckboxDisabled = computed(() => {
  const rowList = props.bulkSelectAcrossPages
    ? state.rowsInView
    : checkableRowsInView.value;

  return rowList.length === 0;
});
const headerCheckboxValue = computed(() => {
  const rowList = props.bulkSelectAcrossPages
    ? state.rowsInView
    : checkableRowsInView.value;

  return rowList.length === 0 ? false : hasSelectedAll.value;
});
const pageLengthOptions = computed(() => {
  const pageLengths = [10, 25, 50, 100];
  return pageLengths.map((length) => ({
    label: length + " items",
    value: length.toString()
  }));
});
const globalFilters = computed(() => {
  const filterNames = tableColumnsConfig.value.map(
    (col) => col.filterName || col.data
  );
  return props.filters?.filter((filter) => !filterNames.includes(filter.name));
});
const setSearchFilters = () => {
  const params = getQueryVariables();
  const filterNames = tableColumnsConfig.value.map(
    (col) => col.filterName || col.data
  );

  filterNames
    .filter((name) => params[name])
    .filter((name) => props.filters.every((filter) => filter.name !== name))
    .map((name) => (state.searches[name] = params[name]));
};
const hasActionsColumn = computed(() => {
  return (
    tableColumnsConfig.value.some((col) => col.data === "actions") &&
    state.resultCount > 0
  );
});

// WATCHERS
watch(stylesLoaded, (val, oldVal) => {
  if (val !== oldVal && val) {
    mount();
  }
});
watch(hasActionsColumn, (val, oldVal) => {
  if (val !== oldVal) {
    // toggle fixed columns when actions columns show/hide.
    // Handles initial loading and no results.
    state.datatable.fixedColumns().end(Number(val));
  }
});
watch(tagsVisible, () => {
  setTimeout(() => {
    setScrollBodyHeight();
  }, 200);
});
watch(
  () => state.rowsInView,
  () => {
    updateSelectedRowsDisplay();
  }
);
watch(
  () => props.bulkSelectIds,
  (val, oldVal) => {
    if (val === oldVal || val === undefined) return;
    if (JSON.stringify(val || []) === JSON.stringify(oldVal || [])) return;
    state.internalSelectedIds = [...val];
  },
  { deep: true }
);
watch(
  () => props.mapRowClass,
  () => updateSelectedRowsDisplay()
);
watch(
  () => state.internalSelectedIds,
  (val) => {
    if (
      JSON.stringify(val || []) === JSON.stringify(props.bulkSelectIds || [])
    ) {
      return;
    }
    updateSelectedRowsDisplay();
    emit("update:bulk-select-ids", val);
  },
  { deep: true }
);
watch(
  () => props.selectedId,
  (val, oldVal) => {
    if (val !== oldVal) {
      selectInitialRow();
    }
  }
);
watch(
  () => state.drawn,
  (val, oldVal) => {
    if (val !== oldVal && val) {
      selectInitialRow();
    }
  }
);
watch(
  () => state.pageLength,
  (val, oldVal) => {
    if (val !== oldVal) {
      state.datatable.page.len(state.pageLength).draw();
    }
  }
);
watch(
  () => state.filtersOpen,
  (val, oldVal) => {
    if (val !== oldVal) {
      // Cache filter state per table source
      storage.merge("tableFilterStates", { [props.source]: val });
      // if (!val) {
      //   nextTick(() => {
      //     $(".col-filter.active").remove();
      //   });
      // }
    }
  }
);
watch(
  () => state.appliedFilters,
  (val, oldVal) => {
    if (val !== oldVal) {
      setTimeout(() => {
        const filtersRowShown = val.length > 0 && oldVal.length === 0;
        const filtersRowHidden = val.length === 0 && oldVal.length > 0;
        if (filtersRowShown || filtersRowHidden) {
          SSUIEventBus.$emit("reposition-dropdown-options");
        }
      }, 300);
    }
  }
);

// LIFECYCLE HOOKS
onBeforeMount(async () => {
  // Import tailwind styles
  await import("./datatable_tailwind.css");
  stylesLoaded.value = true;

  // Set default filter state from cache
  const filterCache = storage.get("tableFilterStates");
  state.filtersOpen = filterCache ? filterCache[props.source] : false;
});
onBeforeUnmount(() => {
  state.isDestroying = true;
  destroyDatatable();
  EventBus.$off("refresh-datatable", refreshData);
  EventBus.$off("clear-datatable-bulk-select", clearAllSelections);
  window.removeEventListener("resize", handleResize);
});

// Make sure to reset the refs before each update.
onBeforeUpdate(() => {
  filterRefs.value = {};
  filterComponentRefs.value = {};
  headerRefs.value = {};
});

// METHODS
const mount = async () => {
  checkInitialProps();
  await setInitialFilters();
  setSearchFilters();
  initTable();
  attachScrollObserver();
  attachRowClickHandler();
  attachPageHandler();
  attachCSVExport();
  initTippy();
  EventBus.$on("refresh-datatable", refreshData);
  EventBus.$on("clear-datatable-bulk-select", clearAllSelections);
  window.addEventListener("resize", handleResize);
  // @ts-expect-error wront type for ImportMeta
  if (import.meta.hot) {
    // @ts-expect-error wront type for ImportMeta
    import.meta.hot.on("vite:afterUpdate", attachHotModuleHandlers);
  }
  // Init Flowbite for search and page select components using data attributes
  window["initFlowbite"]();
};
const columnFilter = (col: ColumnsEntity) =>
  props.filters.find((filter) => filter.name === (col.filterName || col.data));
const refreshData = () => refreshDataWithCallback(props.onReload);
const refreshDataWithCallback = (callback = () => {}) =>
  state.datatable?.ajax.reload(callback, props.resetPaging);
const toggleFiltersOpen = () => {
  state.filtersOpen = !state.filtersOpen;
};
const exportReport = debounce(() => {
  if (props.willEmitDownloadReport()) {
    emit("download-report");
  } else if (props.useDatatableExportCsv) {
    state.datatable.buttons(".buttons-csv").trigger();
  } else {
    API.makeRequest(props.downloadUrl || window.location.pathname, {
      method: "POST",
      data: getQueryVariables()
    }).then((res) => {
      if (res.status === 204) hintTooltip.value?.show();
    });
  }
}, 500);
const removeFilter = async (filter) => {
  await updateQueryParam(filter.name, null, true);
  const filterInput =
    filterComponentRefs.value[`${filter.name}FilterComponent`];
  filterInput?.clearValue();
  if (!filterInput) {
    EventBus.$emit("clear-filter", filter);
  }
  refreshData();
};
const handleSelectAll = () => {
  if (props.bulkSelectAcrossPages) {
    bulkSelectAllAcrossPages();
    return;
  }

  const cachedHasSelectedAll = hasSelectedAll.value;

  checkableRowsInView.value.forEach((row) => {
    const id: never = row[props.bulkSelectKey] as never;
    const indexFound = state.internalSelectedIds.indexOf(id);
    if (cachedHasSelectedAll) {
      state.internalSelectedIds.splice(indexFound, 1);
      emit("row-select", row, false);
    } else {
      if (indexFound === -1) {
        state.internalSelectedIds.push(id);
        emit("row-select", row, true);
      }
    }
  });

  updateSelectedRowsDisplay();
};
const updateSelectedRowsDisplay = () => {
  if (!state.rowsInView.length) return;
  if (!state.datatable) return;
  const table = state.datatable;
  table.rows().every(function (index) {
    const row = table.row(index);
    const id = row.data()[props.bulkSelectKey] as never;
    if (id) row.node().id = id;
    if (state.internalSelectedIds.indexOf(id) > -1) {
      row.node().classList.add("dt-selected-row");
    } else {
      row.node().classList.remove("dt-selected-row");
    }
    if (props.mapRowClass) {
      const rowClass = props.mapRowClass({
        data: row.data(),
        rowIndex: index,
        id,
        row
      });
      if (rowClass) {
        const newRowClasses = [];
        if (typeof rowClass === "string") newRowClasses.push(rowClass as never);
        // @ts-expect-error FIXME
        if (Array.isArray(rowClass)) newRowClasses.push(...rowClass);
        row.node().classList.add(...newRowClasses);
      }
    }
  });
};
const bulkSelectAllAcrossPages = () => {
  if (!hasSelectedAll.value) {
    markAllSelectionsAcrossPages();
  } else {
    clearAllSelections();
  }
};
const markAllSelectionsAcrossPages = () => {
  state.internalSelectedIds = [...state.availableInternalIds];

  state.rowsInView.forEach((row) => {
    emit("row-select", row, true);
  });

  updateSelectedRowsDisplay();
};
const clearAllSelections = () => {
  state.internalSelectedIds.splice(0, state.internalSelectedIds.length);

  state.rowsInView.forEach((row) => {
    emit("row-select", row, false);
  });

  updateSelectedRowsDisplay();
};
const attachScrollObserver = () => {
  const root = $(table.value).closest(".dt-scroll")[0];
  root?.addEventListener("scroll", setScrollShadows);
};
const setScrollShadows = () => {
  nextTick(() => {
    const root = $(table.value).closest(".dt-scroll")[0];
    if (!root) return;
    const scrolledToBottom =
      root.scrollTop + root.clientHeight >= root.scrollHeight;
    state.shadows = {
      left: root.scrollLeft > 0,
      right:
        Math.abs(root.scrollWidth - root.clientWidth - root.scrollLeft) >= 1,
      bottom:
        !scrolledToBottom &&
        (root.scrollTop > 0 || root.scrollHeight > root.clientHeight),
      top: root.scrollTop > 0
    };
  });
};
const fixMisalignedColumns = () => {
  const tableHeaderCont = document.querySelector(
    ".dt-scroll-headInner"
  ) as HTMLElement;
  const headerContWidth = tableHeaderCont?.scrollWidth || 0;
  const headerTable = document.querySelector(
    ".dt-scroll-headInner table"
  ) as HTMLElement;
  const headerWidth = headerTable?.scrollWidth || 0;
  const scrollBodyWidth =
    document.querySelector(".dt-scroll-body")?.scrollWidth;
  if (!tableHeaderCont || !headerTable || !scrollBodyWidth) return;
  if (
    scrollBodyWidth &&
    (headerWidth < scrollBodyWidth || headerContWidth !== scrollBodyWidth)
  ) {
    // Manually adjust header width to account for scrollbars
    headerTable.style.width = `${scrollBodyWidth}px`;
    tableHeaderCont.style.width = `${scrollBodyWidth}px`;
  }
};
const hasFilterApplied = (col: ColumnsEntity) => {
  const hasSearch = state.searches[col.data];
  if (hasSearch) return true;
  const filter = columnFilter(col);
  if (!filter) return false;
  return state.appliedFilters.some((af) => af.name === filter.name);
};
const searchTermLabel = (colName: string) => {
  const column = visibleColumns.value.find((col) => col.data === colName);
  if (!column) return "--";
  return capitalize(column.title || column.data);
};
const filterLabel = (filter: Filter) => {
  switch (filter.type) {
    case "select": {
      const findOption = (value) =>
        filter.options?.find((opt) => opt.value == value) || {
          label: "--",
          value: "--"
        };
      if (filter.multiple && filter.value) {
        // parse selected values
        return filter.value
          ?.split(",")
          .map((val) => findOption(val).label)
          .join(", ");
      }
      return findOption(filter.value).label;
    }
    case "toggle": {
      return filter.options && filter.options[Number(filter.value == "true")];
    }
    default:
      return filter.value;
  }
};
const clearAllSearches = () => {
  // Must remove all column level search values
  $(".column-search-field").val("");
  visibleColumns.value.forEach((col, i) => {
    updateQueryParam(col.data, null, true);
    state.datatable.column(i).search("");
  });
  const updatedUrl = state.datatable.ajax.url().split("?")[0];
  state.datatable.ajax.url(updatedUrl);
  state.searches = {};
};
const clearAllFilters = async () => {
  state.appliedFilters = [];
  await router.replace({ query: {} });
  // Must clear all filter query params
  EventBus.$emit("clear-datatable-filters");
  clearAllSearches();
  state.datatable.columns().draw();
};
const clearSearch = async (columnName: string) => {
  const value = state.searches[columnName];
  await updateQueryParam(columnName, null, true);
  delete state.searches[columnName];
  localStorage.removeItem(`search_${columnName}`);
  if (state.datatable.ajax.url().includes(columnName)) {
    const updatedUrl = state.datatable.ajax
      .url()
      .replace(`${columnName}=${value}`, "");
    state.datatable.ajax.url(updatedUrl);
  }
  performColSearch("", columnName);
};
const performColSearch = debounce((value: string, columnName: string) => {
  const index = tableColumnsConfig.value.findIndex(
    (_col) => _col.data === columnName
  );
  state.datatable.column(index).search(value).draw();
}, 250);
const getSearchPlaceholder = (col: ColumnsEntity) => {
  if (col.data === "address") {
    return "Search Street";
  }
  return col.searchPlaceholder || "Search";
};
const getColumnSearchValue = (columnName: string) =>
  state.searches[columnName] || "";
const handleColSearch = async (value: string, columnName: string) => {
  if (!value) {
    return clearSearch(columnName);
  }
  state.searches[columnName] = value;
  if (props.persistSearch) {
    localStorage.setItem(`search_${columnName}`, value);
  }
  await updateQueryParam(columnName, value, true);
  performColSearch(value, columnName);
};
const initTable = () => {
  if (state.datatable?.destroy) {
    state.datatable.destroy();
  }
  const params = new URLSearchParams(window.location.search);
  const searchTerm = params.get(CALL_MODAL_SEARCH_TERM_KEY);
  if (window["DataTable"]) {
    window["DataTable"].defaults.column.orderSequence = ["asc", "desc"];
  }
  state.datatable = $(table.value).DataTable({
    ...configDefaults,
    ...props.config,
    fixedColumns: { start: "0", end: hasActionsColumn.value ? "1" : "0" },
    searchCols: state.searchCols,
    pageLength: state.pageLength,
    columns: tableColumnsConfig.value,
    search: {
      search: searchTerm && searchTerm !== "" ? searchTerm : null
    },
    ajax: {
      url: props.source,
      data: props.mapRecords
    },
    footerCallback: function () {
      if (state.isDestroying) return;
      setupFooter();
    },
    initComplete: function () {
      if (state.isDestroying) return;
      updateQueryParam(CALL_MODAL_SEARCH_TERM_KEY, null, true);
      // attachScrollableHandler();
      emit("after-init");
    },
    preDrawCallback: function () {
      if (state.isDestroying) return;
      // Forces a remount of custom vue components
      state.drawing = true;
      nextTick(() => (state.drawing = false));
      onBeforeDraw();
    },
    drawCallback: attachRenderHandler,
    infoCallback: function (oSettings, iStart, iEnd, iMax, iTotal) {
      // Fixes pricebook tasks price links in table after each draw
      document.dispatchEvent(new CustomEvent("datatable:draw"));
      let count = "";
      if (iEnd == 0) {
        count = `<span class="font-semibold text-gray-900 dark:text-white">0</span> results`;
      } else {
        const format = (num) => parseInt(num, 10).toLocaleString();
        count = `Showing <span class="font-semibold text-gray-900 dark:text-white">${format(
          iStart
        )}</span><span class="font-semibold text-gray-900 dark:text-white">-</span>
          <span class="font-semibold text-gray-900 dark:text-white">${format(
            iEnd
          )}</span> of <span class="font-semibold text-gray-900 dark:text-white">${format(
          iTotal
        )}</span>`;
      }
      // Always include the pageSelectPortal for mounting DatatablePageLength component
      return count + `<div id="pageSelectPortal-${props.id}"></div>`;
    }
  });
};
const setupFooter = () => {
  state.hasAggregates = true;
  const aggregates = (state.datatable.ajax.json() || {}).aggregates || {};
  if (props.bulkSelectAcrossPages) {
    state.availableInternalIds = (
      state.datatable.ajax.json() || {}
    ).full_internal_ids;
  }
  if (!state.datatable.data().toArray().length || isEmpty(aggregates)) {
    state.hasAggregates = false;
    setupNoResultsMessage();
    return;
  }
  state.datatable.context[0].aoColumns
    .filter((item) => item.bVisible)
    .forEach((col, idx) => {
      const aggValue = aggregates[col.data];
      if (!isFalsy(aggValue) || col.nullAggregate) {
        const header = col.aggregateTitle || `Total ${capitalize(col.data)}`;
        $(`th:eq(${idx})`, state.datatable.table().footer()).html(header);
        if (col.aggregateComponent) {
          const { component, rootId, propName, options } =
            col.aggregateComponent;
          const props = JSON.stringify({
            [propName]: aggValue
          });
          const root = `<div id="${rootId}" data=${props}></div>`;
          $(`td:eq(${idx})`, state.datatable.table().footer()).html(root);
          const nodes = document.querySelectorAll(`#${rootId}`);
          nodes.forEach((node) => {
            renderVueComponent(node, component, options);
          });
        } else {
          const aggregateDisplay = col.aggregateRender
            ? col.aggregateRender(aggValue)
            : aggValue;
          $(`td:eq(${idx})`, state.datatable.table().footer()).html(
            aggregateDisplay
          );
        }
      }
    });
};
const setupNoResultsMessage = () => {
  setTimeout(() => {
    const dtEmpty = table.value.querySelector(".dt-empty");
    if (!dtEmpty) return;
    if (state.isDestroying) return;

    const noResultsMessageEl = document.createElement("div");
    noResultsMessageEl.classList.add("no-table-results-message");
    noResultsMessageEl.innerHTML = "No matching records found";
    dtEmpty.innerHTML = "";
    dtEmpty.appendChild(noResultsMessageEl);
    fixNoResultsMessageDisplay();
  });
};
const fixNoResultsMessageDisplay = () => {
  const datatable = $(datatableRef.value);
  const scrollableWrapperEl = datatable.find(".dt-scroll-body")[0];
  const scrollHeadWidth = datatable.find(".dt-scroll-head")[0].scrollWidth;
  const innerTable = scrollableWrapperEl.childNodes[0] as HTMLElement;
  /* Fixes issue where no data message isn't full length */
  if (innerTable.scrollWidth < scrollHeadWidth) {
    innerTable.style.minWidth = `${scrollHeadWidth}px`;
  }
  scrollableWrapperEl
    .querySelectorAll(".no-table-results-message")
    .forEach((el) => {
      (el as HTMLElement).style.width = `${
        (scrollableWrapperEl.parentNode as HTMLElement).getBoundingClientRect()
          .width -
        42 + // offset for padding
        scrollableWrapperEl.scrollLeft
      }px`;
    });
};
const onBeforeDraw = () => {
  setLoadingPosition();
  state.rowSelectionRange = [null, null];
  // Clear custom Vue apps array to prevent stale components
  state.customRowVueApps = [];
};
const setLoadingPosition = () => {
  try {
    const datatableIndexMatch = table.value?.id || "dt";
    const leftOffset =
      $(`#${datatableIndexMatch}`).closest(".dt-scroll").scrollLeft() || 0;
    const processingSpinner = $(`#${datatableIndexMatch}_processing`);
    if (processingSpinner.length === 0) return;

    processingSpinner.css("left", `calc(50% + ${leftOffset}px)`);
  } catch (e) {
    // no-op
  }
};
const attachRenderHandler = () => {
  if (!props.bulkSelectKeepAlive) {
    state.internalSelectedIds = [];
  }
  state.rowsInView = state.datatable.rows().data().toArray();
  const totalRows = state.datatable.ajax.json().recordsTotal;
  emit("draw-complete", {
    totalRows
  });
  state.drawn = true;
  renderVueComponents();
  setupFooter();
  state.searches = { ...state.searches };
  state.resultCount = state.datatable.rows().data().length;
  setAppliedFilters();
  setScrollBodyHeight();
  attachScrollObserver();
  attachColumnHoverHandler();
  nextTick(() => {
    setTimeout(() => {
      // Force fake scroll event to reposition filter menus
      document.dispatchEvent(new CustomEvent("scroll"));
      // Trigger resize to apply actions column shadow
      window.dispatchEvent(new CustomEvent("resize"));
      // Fix column alignment after drawing shadows
      fixMisalignedColumns();
      // Fix datatables column alignment issue
      state.datatable.columns.adjust();
    });
  });
};
const renderVueComponents = () => {
  if (props.config.vueComponents) {
    props.config.vueComponents.forEach((componentDef) => {
      const { rootId, component, options = {} } = componentDef;
      const nodes = document.querySelectorAll(`#${rootId}`);
      nodes.forEach((node) => {
        const data = extractProps(node, options);
        // eslint-disable-next-line vue/one-component-per-file
        const vueComponent = defineComponent({
          setup() {
            return () => h(component, { ...data });
          }
        });

        renderVueComponent(node, vueComponent, { store: options?.store });
      });
    });
  }
  if (state.customRowVueApps.length) {
    state.customRowVueApps.forEach(({ column, component }, rowIndex) => {
      const rootId = `datatable-custom-column-${column}-${rowIndex}`;
      const nodes = document.querySelectorAll(`.${rootId}`);
      nodes.forEach((node) => {
        // For checkbox components, mount directly without prop extraction
        if (column === "bulk-select-checkbox") {
          // Check if already mounted
          if (node.getAttribute("data-v-app") !== null) return;

          const app = createApp(component);
          if (store) {
            app.use(store);
          }
          app.mount(node);
        } else {
          renderVueComponent(node, component, { store });
        }
      });
    });
  }
};
const handleSelect = (row) => {
  const val = row[props.bulkSelectKey];
  const indexFound = state.internalSelectedIds.indexOf(val as never);
  if (indexFound > -1) {
    state.internalSelectedIds.splice(indexFound, 1);
    emit("row-select", row, false);
  } else {
    state.internalSelectedIds.push(val as never);
    emit("row-select", row, true);
  }
  updateSelectedRowsDisplay();
};
const handleSelectRange = (e, row) => {
  // Remove any selected text
  document.getSelection()?.removeAllRanges();
  const internalId = row.data()[props.bulkSelectKey];
  const isChecked = state.internalSelectedIds.indexOf(internalId as never) > -1;
  if (!e.shiftKey) {
    state.rowSelectionRange[0] = row.index().row;
    state.rowSelectionRange[1] = null;
    state.lastCheckedValue = !isChecked;
    state.usedShiftKeyLast = false;
    return;
  }
  e.preventDefault();
  if (state.usedShiftKeyLast) {
    state.rowSelectionRange[0] = row.index().row;
    selectRange(state.rowSelectionRange, state.lastCheckedValue);
    state.rowSelectionRange[1] = row.index().row;
  } else {
    state.rowSelectionRange[1] = row.index().row;
    selectRange(state.rowSelectionRange, state.lastCheckedValue);
  }
  state.usedShiftKeyLast = true;
};
/**
 * Check / Uncheck rows from range [startIndex, endIndex]
 * Ignores disabled rows
 * @param {array} customRange
 */
const selectRange = (customRange, checkedValue) => {
  if (!Number.isInteger(customRange[0]) || !Number.isInteger(customRange[1])) {
    return;
  }
  const rows = state.datatable.rows();
  const range = customRange.slice(0).sort((a, b) => a - b);

  // Go through the range and build the row data
  const rowToChecked = [];
  for (let i = range[0]; i <= range[1]; i++) {
    const rowData = rows.data()[i];
    if (!rowData) continue;
    const internalId = rowData[props.bulkSelectKey];
    if (rowData.checkbox_disabled === "true") continue;
    rowToChecked.push({
      internalId,
      checked: state.internalSelectedIds.indexOf(internalId as never) > -1,
      rowData,
      node: rows.nodes()[i]
    } as never);
  }

  // If every row is checked in the selection, we should do the opposite
  // and overide our original checked value
  let newCheckedValue = checkedValue;
  if (rowToChecked.every((row: RowEntity) => row.checked))
    newCheckedValue = false;
  if (rowToChecked.every((row: RowEntity) => !row.checked))
    newCheckedValue = true;
  state.lastCheckedValue = newCheckedValue;

  // Go through each row and check / uncheck
  rowToChecked.forEach((item: RowEntity) => {
    const internalSelectedIdIndex = state.internalSelectedIds.indexOf(
      item.internalId as never
    );
    if (newCheckedValue) {
      if (internalSelectedIdIndex === -1) {
        state.internalSelectedIds.push(item.internalId as never);
        emit("row-select", item.rowData, true);
      }
      item.node.classList.add("dt-selected-row");
    } else {
      if (internalSelectedIdIndex > -1) {
        state.internalSelectedIds.splice(internalSelectedIdIndex, 1);
        emit("row-select", item.rowData, false);
      }
      item.node.classList.remove("dt-selected-row");
    }
  });
};
const setAppliedFilters = () => {
  const params = getQueryVariables();
  state.appliedFilters = props.filters
    .filter((filter) => params[filter.name])
    .map((filter) => ({ ...filter, value: params[filter.name] }));
};
const setScrollBodyHeight = () => {
  nextTick(() => {
    const body = $(table.value).closest(".dt-scroll")[0];
    if (!body) return;
    if (props.minHeight) {
      body.style.minHeight = `${props.minHeight}px`;
    }
    if (!props.autoHeight) return;

    const bodyTopOffset = body.getBoundingClientRect().top;

    const SCROLL_OFFSET = breakpoints().md
      ? STACKED_FOOTER_HEIGHT
      : UNSTACKED_FOOTER_HEIGHT;

    const calculatedHeight = window.innerHeight - bodyTopOffset - SCROLL_OFFSET;

    const height = props.maxHeight
      ? Math.min(calculatedHeight, props.maxHeight)
      : calculatedHeight;

    body.style.height = `${height}px`;
  });
};
const checkInitialProps = () => {
  if (props.bulkSelect) {
    if (props.bulkSelectIds?.length && !props.bulkSelectKeepAlive) {
      console.error(
        "Missing `bulk-select-keep-alive` property. Id's passed into the datatable will reset after first render."
      );
    }
  }
};

const setInitialFilters = async () => {
  const params = getQueryVariables();
  const defaultsToSet = {};

  props.filters.forEach((f) => {
    if (!isFalsy(f.default_value) && isFalsy(params[f.name])) {
      defaultsToSet[f.name] = f.default_value ?? null;
    }
  });

  if (!router) return;

  const newQuery = {
    ...(router.currentRoute && router.currentRoute.value
      ? router.currentRoute.value.query
      : params),
    ...defaultsToSet
  };

  if (params.scheduled_time === "null") {
    delete newQuery.scheduled_time;
  }

  if (newQuery === router?.currentRoute.value?.query) return;
  await router.replace({ path: window.location.pathname, query: newQuery });
  await nextTick();
};

const attachRowClickHandler = () => {
  $(tbody.value).on("click", "tr", function () {
    const row = state.datatable.row(this).data();
    if (row) {
      if (props.selectable) {
        setSelectedRow(this);
      }
      // Emit row click with row data
      emit("row-click", row);
    }
  });
};
const setSelectedRow = (node) => {
  if (node) {
    // Highlight selected row
    state.datatable.$("tr.dt-selected-row").removeClass("dt-selected-row");
    $(node).toggleClass("dt-selected-row");
    state.selectedNode = node;
  }
};
const attachPageHandler = () => {
  // emits event after table is rendered for the first time
  $(table.value).on("page.dt", () => {
    emit("page-change");
    if (props.selectedId && !state.selectedNode) {
      selectInitialRow();
    }
  });
};
const selectInitialRow = () => {
  if (props.selectedId) {
    const node = findNodeById(props.selectedId);
    setSelectedRow(node);
  }
};
const findNodeById = (id) => {
  return state.datatable.row(`#${id}`).node();
};
const initTippy = () => {
  // Initializes tippy tooltips when table is rendered
  $(table.value).on("draw.dt", () => {
    tippy("[data-tippy-content]");
    tippy("th:not(.dt-orderable-none) .dt-column-order", {
      content: "Sort"
    });
  });
};
const handleResize = () => {
  emit("resize");
  setScrollBodyHeight();
  setScrollShadows();
  state.datatable.columns.adjust();
  fixNoResultsMessageDisplay();
};
const attachHotModuleHandlers = () => {
  state.customRowVueApps.forEach((app: any) => {
    if (app && app.$destroy) app.$destroy();
  });
};
const attachColumnHoverHandler = () => {
  nextTick(() => {
    const table = state.datatable;
    $(datatableRef.value).on(
      "mouseenter ",
      ".dt-scroll-head table th",
      function () {
        // $(".datatable-v3 .dt-scroll-head table").on("mouseenter ", "th", function () {
        // Remove highlight from all columns
        table
          .columns()
          .nodes()
          .flatten() // Reduce to a 1D array
          .to$() // Convert to a jQuery object
          .removeClass("cell-hover");

        // Add highlight to mouseover column
        table
          .column(this)
          .nodes()
          .to$() // Convert to a jQuery object
          .addClass("cell-hover");

        // Remove highlight from all headers
        table.columns().header().flatten().to$().removeClass("cell-hover");

        // Add highlight to mouseover headers
        table.columns(this).header().to$().addClass("cell-hover");
      }
    );
    $(".datatable-v3 .dt-scroll-head table").on(
      "mouseleave ",
      "th",
      function () {
        $(".datatable-v3 table td").removeClass("cell-hover");
        $(".datatable-v3 table th").removeClass("cell-hover");
      }
    );
  });
};
const destroyDatatable = () => {
  if (!state.datatable) return;

  $(table.value).off();
  $(tbody.value).off();
  state.datatable.destroy(true);
};
const handleFilterFocus = (e) => {
  // Triggers dropdowns to check if they should open or close
  // This is needed because we are stopping propagation of events
  // on filter clicks otherwise we would sort everytime we click a filter
  SSUIEventBus.$emit("hide-all-dropdowns", e);
};
const getSortClasses = (col: ColumnsEntity, columnIdx: number): string => {
  const classes: string[] = [];
  // Add default datatable ordering classes
  if (col.orderable !== false) {
    classes.push("dt-orderable-asc");
    classes.push("dt-orderable-desc");
  } else {
    classes.push("dt-orderable-none");
  }
  // Get current sort order and set active class
  const getActiveSortClass = (order: Config["order"], colIdx) => {
    if (!order || !order[0]) return "";
    return order.reduce((classes, _order) => {
      if (!_order || !_order[0]) return classes;
      const sortedIdx = _order[0];
      const direction = _order[1];
      if (sortedIdx === colIdx) {
        classes = `dt-ordering-${direction}`;
      }
      return classes;
    }, "");
  };
  const currentOrder = state.datatable
    ? state.datatable.order()
    : props.config.order;
  const activeClass = getActiveSortClass(currentOrder, columnIdx);
  if (activeClass) {
    classes.push(activeClass);
  }
  return classes.join(" ");
};

const attachCSVExport = () => {
  new $.fn.DataTable.Buttons(state.datatable, {
    dom: {
      button: {
        className: ""
      }
    },
    buttons: [
      {
        extend: "csv",
        text: "Export CSV",
        filename: props.csvFileName,
        className: "dropdown-item clickeable",
        exportOptions: {
          columns: "thead th:not(.actions-column-header)",
          format: {
            body(data) {
              // Remove HTML tags when exporting to CSV
              return data.replace(/<([^>]+)>/gi, "");
            }
          }
        }
      }
    ]
  })
    .container()
    .appendTo($("#dropdown-menu-buttons"));
  return $(".dt-buttons").contents().unwrap();
};

defineExpose({
  markAllSelectionsAcrossPages,
  refreshData,
  refreshDataWithCallback,
  exportReport
});
</script>

<style lang="scss">
.dataTables_scrollHeadInner {
  width: 100% !important;
}
.dataTables_scrollHeadInner table {
  width: 100% !important;
}
.datatable-v3 .date-picker-input {
  padding-right: 30px;
  min-width: 190px;
}

.dt-scroll-head,
.dt-scroll-body,
.dt-scroll-foot {
  overflow: visible !important;
}
.dt-scroll-head {
  position: sticky !important;
  top: 0;
  z-index: 2;
}
.dt-scroll {
  overflow: auto;
}
.column-search-field {
  min-width: 90px;
}
div.dt-container div.row > * {
  margin-bottom: 0 !important;
}
</style>
